import * as React from "react";
import { ReviewTileItem, Impact } from "@/types/tile.types";
import { useDismissAllTiles } from "@/hooks/useDismissAllTiles";
import { useBulkWriteIt } from "@/hooks/useBulkWriteIt";
import { useBulkInsert } from "@/hooks/useBulkInsert";
import { useBulkCopy } from "@/hooks/useBulkCopy";
import { useCreateDocumentFromTiles } from "@/hooks/useCreateDocumentFromTiles";
import { useAddAllAsComments } from "@/hooks/useAddAllAsComments";
import { BulkWriteItProgressProvider, useBulkWriteItProgress } from "./BulkWriteItProgressContext";
import { BulkWriteItProgressDialog } from "./BulkWriteItProgressDialog";
import { BulkActionsProgressDialog } from "./BulkActionsProgressDialog";
import {
  makeBulkResultHandler,
  makeBulkCopyHandler,
  computeProgressInfo,
  BulkActionButtons,
  buildBulkActions,
  executeBulkAction,
  executeCloseAll,
} from "./TilesBulkActions.utils";

export function useBulkActionsHooks(
  setIsAddAsCommentsDialogOpen: (v: boolean) => void,
  setIsBulkInsertDialogOpen: (v: boolean) => void
) {
  const dismissAllTilesMutation = useDismissAllTiles();
  const { mutate: mutateBulkWriteIt, isPending: isPendingBulkWriteIt } = useBulkWriteIt();
  const { createDocumentFromTiles, isCreatingDocument: isCreatingDocumentFromTiles } =
    useCreateDocumentFromTiles();
  const writeItProgress = useBulkWriteItProgress();

  const addAsCommentsHook = useAddAllAsComments(
    makeBulkResultHandler(setIsAddAsCommentsDialogOpen)
  );
  const bulkInsertHook = useBulkInsert(makeBulkResultHandler(setIsBulkInsertDialogOpen));
  const bulkCopyHook = useBulkCopy(makeBulkCopyHandler());

  const isAnyOperationInProgress =
    addAsCommentsHook.isProcessing ||
    dismissAllTilesMutation.isPending ||
    isPendingBulkWriteIt ||
    bulkInsertHook.isProcessing ||
    bulkCopyHook.isProcessing ||
    isCreatingDocumentFromTiles;

  return {
    dismissAllTilesMutation,
    mutateBulkWriteIt,
    isPendingBulkWriteIt,
    createDocumentFromTiles,
    isCreatingDocumentFromTiles,
    writeItProgress,
    addAsCommentsHook,
    bulkInsertHook,
    bulkCopyHook,
    isAnyOperationInProgress,
  };
}

interface ProgressDialogsProps {
  hooks: ReturnType<typeof useBulkActionsHooks>;
  isAddAsCommentsDialogOpen: boolean;
  setIsAddAsCommentsDialogOpen: (v: boolean) => void;
  isBulkInsertDialogOpen: boolean;
  setIsBulkInsertDialogOpen: (v: boolean) => void;
}

const ProgressDialogs: React.FC<ProgressDialogsProps> = ({
  hooks,
  isAddAsCommentsDialogOpen,
  setIsAddAsCommentsDialogOpen,
  isBulkInsertDialogOpen,
  setIsBulkInsertDialogOpen,
}) => {
  const commentsProgress = computeProgressInfo(hooks.addAsCommentsHook.progress, "tiles");
  const insertProgress = computeProgressInfo(hooks.bulkInsertHook.progress, "tiles");
  const { isOpen, progress, statusMessage, failedTiles, hideProgress } = hooks.writeItProgress;

  return (
    <>
      <BulkWriteItProgressDialog
        isOpen={isOpen}
        onClose={hideProgress}
        progress={progress}
        statusMessage={statusMessage}
        failedTiles={failedTiles}
      />
      <BulkActionsProgressDialog
        isOpen={isAddAsCommentsDialogOpen}
        onClose={() => setIsAddAsCommentsDialogOpen(false)}
        progress={commentsProgress.percentage}
        message={commentsProgress.message}
        title="Adding Comments"
      />
      <BulkActionsProgressDialog
        isOpen={isBulkInsertDialogOpen}
        onClose={() => setIsBulkInsertDialogOpen(false)}
        progress={insertProgress.percentage}
        message={insertProgress.message}
        title="Inserting Tiles"
      />
    </>
  );
};

interface TilesBulkActionsProps {
  filteredItems: ReviewTileItem[];
  documentId: string;
  selectedQueryTerms: string[];
  onOperationStateChange?: (isProcessing: boolean) => void;
  impactFilter: Impact;
}

export const TilesBulkActions: React.FC<TilesBulkActionsProps> = ({
  filteredItems,
  documentId,
  selectedQueryTerms,
  onOperationStateChange,
  impactFilter,
}) => {
  return (
    <BulkWriteItProgressProvider>
      <TilesBulkActionsContent
        filteredItems={filteredItems}
        documentId={documentId}
        selectedQueryTerms={selectedQueryTerms}
        onOperationStateChange={onOperationStateChange}
        impactFilter={impactFilter}
      />
    </BulkWriteItProgressProvider>
  );
};

function useBulkActionsCallbacks(
  filteredItems: ReviewTileItem[],
  documentId: string,
  selectedQueryTerms: string[],
  hooks: ReturnType<typeof useBulkActionsHooks>,
  impactFilter: string,
  setIsBulkInsertDialogOpen: React.Dispatch<React.SetStateAction<boolean>>,
  setIsAddAsCommentsDialogOpen: React.Dispatch<React.SetStateAction<boolean>>
) {
  const impactText = impactFilter === "ALL" ? "all" : impactFilter.toLowerCase();

  const handleBulkAction = React.useCallback(
    async (action: string) =>
      executeBulkAction(
        action,
        filteredItems,
        documentId,
        hooks,
        setIsBulkInsertDialogOpen,
        setIsAddAsCommentsDialogOpen
      ),
    [filteredItems, documentId, hooks]
  );

  const handleCloseAll = React.useCallback(
    async () =>
      executeCloseAll(filteredItems, documentId, selectedQueryTerms, hooks.dismissAllTilesMutation),
    [filteredItems, documentId, selectedQueryTerms, hooks.dismissAllTilesMutation]
  );

  const bulkActions = React.useMemo(
    () =>
      buildBulkActions({
        impactText,
        disabled: hooks.isAnyOperationInProgress || filteredItems.length === 0,
        bulkInsertLoading: hooks.bulkInsertHook.isProcessing,
        addAsCommentsLoading: hooks.addAsCommentsHook.isProcessing,
        isCreatingDocument: hooks.isCreatingDocumentFromTiles,
        bulkCopyLoading: hooks.bulkCopyHook.isProcessing,
        bulkWriteItLoading: hooks.isPendingBulkWriteIt,
        dismissLoading: hooks.dismissAllTilesMutation.isPending,
        handleBulkAction,
        handleCloseAll,
      }),
    [
      filteredItems.length,
      hooks.isAnyOperationInProgress,
      hooks.addAsCommentsHook.isProcessing,
      hooks.bulkInsertHook.isProcessing,
      hooks.bulkCopyHook.isProcessing,
      hooks.isCreatingDocumentFromTiles,
      hooks.dismissAllTilesMutation.isPending,
      hooks.isPendingBulkWriteIt,
      impactText,
      handleBulkAction,
      handleCloseAll,
    ]
  );

  return bulkActions;
}

const TilesBulkActionsContent: React.FC<TilesBulkActionsProps> = ({
  filteredItems,
  documentId,
  selectedQueryTerms,
  onOperationStateChange,
  impactFilter,
}) => {
  const [isAddAsCommentsDialogOpen, setIsAddAsCommentsDialogOpen] = React.useState(false);
  const [isBulkInsertDialogOpen, setIsBulkInsertDialogOpen] = React.useState(false);
  const hooks = useBulkActionsHooks(setIsAddAsCommentsDialogOpen, setIsBulkInsertDialogOpen);

  React.useEffect(() => {
    onOperationStateChange?.(hooks.isAnyOperationInProgress);
  }, [hooks.isAnyOperationInProgress, onOperationStateChange]);

  const bulkActions = useBulkActionsCallbacks(
    filteredItems,
    documentId,
    selectedQueryTerms,
    hooks,
    impactFilter,
    setIsBulkInsertDialogOpen,
    setIsAddAsCommentsDialogOpen
  );

  return (
    <>
      <BulkActionButtons actions={bulkActions} />
      <ProgressDialogs
        hooks={hooks}
        isAddAsCommentsDialogOpen={isAddAsCommentsDialogOpen}
        setIsAddAsCommentsDialogOpen={setIsAddAsCommentsDialogOpen}
        isBulkInsertDialogOpen={isBulkInsertDialogOpen}
        setIsBulkInsertDialogOpen={setIsBulkInsertDialogOpen}
      />
    </>
  );
};

export default TilesBulkActions;
